import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Copy, Play, CheckCircle, XCircle, Code, Book, Zap } from 'lucide-react';
import Footer from './footer';
import Header from './header';
import DeveloperAPI from './developerAPI'; // Assuming you have a separate component for API testing
import ApiDev from './developerAPIRequest';
const ApiDocsPage = () => {
  const [activeSection, setActiveSection] = useState('getting-started');
  const [expandedEndpoints, setExpandedEndpoints] = useState({});
  const [testResults, setTestResults] = useState({});
  const [loading, setLoading] = useState({});

  const toggleEndpoint = (endpoint) => {
    setExpandedEndpoints(prev => ({
      ...prev,
      [endpoint]: !prev[endpoint]
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const testEndpoint = async (endpoint, method, url, headers, body) => {
    setLoading(prev => ({ ...prev, [endpoint]: true }));

    // Simulate API call
    setTimeout(() => {
      const mockResponse = {
        status: 200,
        data: {
          success: true,
          message: "API call successful",
          data: method === 'GET' ? { id: 1, name: "John Doe", email: "john@anvitechsolutions.com" } : { id: Date.now() },
          timestamp: new Date().toISOString()
        }
      };

      setTestResults(prev => ({
        ...prev,
        [endpoint]: mockResponse
      }));
      setLoading(prev => ({ ...prev, [endpoint]: false }));
    }, 1500);
  };

  const sections = [
    { id: 'getting-started', title: 'Getting Started', icon: Book },
    { id: 'authentication', title: 'Authentication', icon: CheckCircle },
    { id: 'endpoints', title: 'API Endpoints', icon: Code },
    { id: 'testing', title: 'API Testing', icon: Zap }
  ];

  const endpoints = [
    {
      id: 'get-users',
      method: 'GET',
      path: '/api/users',
      title: 'Get Users',
      description: 'Retrieve a list of all users',
      parameters: [
        { name: 'page', type: 'integer', required: false, description: 'Page number for pagination' },
        { name: 'limit', type: 'integer', required: false, description: 'Number of items per page' }
      ]
    },
    {
      id: 'create-user',
      method: 'POST',
      path: '/api/users',
      title: 'Create User',
      description: 'Create a new user account',
      parameters: [
        { name: 'name', type: 'string', required: true, description: 'User full name' },
        { name: 'email', type: 'string', required: true, description: 'User email address' },
        { name: 'password', type: 'string', required: true, description: 'User password' }
      ]
    },
    {
      id: 'get-user',
      method: 'GET',
      path: '/api/users/{id}',
      title: 'Get User by ID',
      description: 'Retrieve a specific user by their ID',
      parameters: [
        { name: 'id', type: 'integer', required: true, description: 'User ID' }
      ]
    },
    {
      id: 'update-user',
      method: 'PUT',
      path: '/api/users/{id}',
      title: 'Update User',
      description: 'Update an existing user',
      parameters: [
        { name: 'id', type: 'integer', required: true, description: 'User ID' },
        { name: 'name', type: 'string', required: false, description: 'User full name' },
        { name: 'email', type: 'string', required: false, description: 'User email address' }
      ]
    }
  ];

  const getMethodColor = (method) => {
    const colors = {
      GET: 'bg-green-100 text-green-800 border-green-200',
      POST: 'bg-blue-100 text-blue-800 border-blue-200',
      PUT: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      DELETE: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[method] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <nav className="w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contents</h3>
              <ul className="space-y-2">
                {sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <li key={section.id}>
                      <button
                        onClick={() => setActiveSection(section.id)}
                        className={`w-full flex items-center px-3 py-2 text-left rounded-md transition-colors ${activeSection === section.id
                            ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-700'
                            : 'text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        <Icon className="w-4 h-4 mr-3" />
                        {section.title}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </nav>

          {/* Main Content */}
          <div className="flex-1">
            {activeSection === 'getting-started' && (
              <div className="bg-white rounded-lg shadow-sm border p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Getting Started</h2>

                <div className="prose max-w-none">
                  <p className="text-gray-600 text-lg mb-6">
                    Welcome to our API documentation. This guide will help you integrate with our services quickly and efficiently.
                  </p>

                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Base URL</h3>
                  <div className="bg-gray-100 rounded-lg p-4 mb-6">
                    <code className="text-sm text-gray-800">https://api.anvitechsolutions.com/v2</code>
                    <button
                      onClick={() => copyToClipboard('https://api.anvitechsolutions.com/v2')}
                      className="ml-2 p-1 hover:bg-gray-200 rounded"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>

                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Example</h3>
                  <div className="bg-gray-900 rounded-lg p-4 mb-6">
                    <pre className="text-green-400 text-sm overflow-x-auto">
                      {`curl -X GET "https://api.anvitechsolutions.com/v2/users" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"`}
                    </pre>
                  </div>

                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                    <p className="text-blue-700">
                      <strong>Note:</strong> All API requests must include proper authentication headers.
                      See the Authentication section for details.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'authentication' && (
              <div className="bg-white rounded-lg shadow-sm border p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Authentication</h2>

                <div className="prose max-w-none">
                  <p className="text-gray-600 text-lg mb-6">
                    Our API uses Bearer token authentication. Include your API key in the Authorization header.
                  </p>

                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Header Format</h3>
                  <div className="bg-gray-900 rounded-lg p-4 mb-6">
                    <pre className="text-green-400 text-sm">
                      {`Authorization: Bearer YOUR_API_KEY`}
                    </pre>
                  </div>

                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Getting an API Key</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-600">
                    <li>Sign up for an account on our developer portal</li>
                    <li>Navigate to the API Keys section</li>
                    <li>Generate a new API key</li>
                    <li>Copy and securely store your key</li>
                  </ol>

                  <div className="bg-red-50 border-l-4 border-red-400 p-4 mt-6">
                    <p className="text-red-700">
                      <strong>Security:</strong> Never expose your API key in client-side code or public repositories.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'endpoints' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm border p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">API Endpoints</h2>
                  <p className="text-gray-600 mb-8">
                    Explore our available endpoints below. Click on any endpoint to see detailed information and test it directly.
                  </p>
                </div>

                {endpoints.map((endpoint) => (
                  <div key={endpoint.id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                    <div
                      className="p-6 cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleEndpoint(endpoint.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <span className={`px-3 py-1 rounded-md text-sm font-medium border ${getMethodColor(endpoint.method)}`}>
                            {endpoint.method}
                          </span>
                          <code className="text-lg font-mono text-gray-800">{endpoint.path}</code>
                          <span className="text-gray-600">{endpoint.title}</span>
                        </div>
                        {expandedEndpoints[endpoint.id] ?
                          <ChevronDown className="w-5 h-5 text-gray-400" /> :
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        }
                      </div>
                    </div>

                    {expandedEndpoints[endpoint.id] && (
                      <div className="border-t bg-gray-50 p-6">
                        <p className="text-gray-600 mb-6">{endpoint.description}</p>

                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Parameters</h4>
                        <div className="overflow-x-auto mb-6">
                          <table className="min-w-full bg-white rounded-lg">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Name</th>
                                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Type</th>
                                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Required</th>
                                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Description</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {endpoint.parameters.map((param, index) => (
                                <tr key={index}>
                                  <td className="px-4 py-2 text-sm font-mono text-gray-800">{param.name}</td>
                                  <td className="px-4 py-2 text-sm text-gray-600">{param.type}</td>
                                  <td className="px-4 py-2 text-sm">
                                    {param.required ? (
                                      <span className="text-red-600 font-medium">Yes</span>
                                    ) : (
                                      <span className="text-gray-500">No</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-600">{param.description}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* <button
                          onClick={() => testEndpoint(endpoint.id, endpoint.method, endpoint.path, {}, {})}
                          disabled={loading[endpoint.id]}
                          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Play className="w-4 h-4 mr-2" />
                          {loading[endpoint.id] ? 'Testing...' : 'Test Endpoint'}
                        </button> */}

                        {testResults[endpoint.id] && (
                          <div className="mt-6 bg-gray-900 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-green-400 font-medium">Response</span>
                              <span className="text-green-400 text-sm">Status: {testResults[endpoint.id].status}</span>
                            </div>
                            <pre className="text-green-400 text-sm overflow-x-auto">
                              {JSON.stringify(testResults[endpoint.id].data, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {activeSection === 'testing' && (
              <ApiDev />
              // <DeveloperAPI />
              //   <div className="bg-white rounded-lg shadow-sm border p-8">
              //     <h2 className="text-2xl font-bold text-gray-900 mb-6">API Testing</h2>

              //     <div className="prose max-w-none">
              //       <p className="text-gray-600 text-lg mb-6">
              //         Test our API endpoints directly from this documentation. This interactive testing tool helps you understand our API responses.
              //       </p>

              //       <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
              //         <p className="text-yellow-700">
              //           <strong>Note:</strong> The testing tool above uses mock responses for demonstration. 
              //           In a real implementation, these would make actual API calls to your backend.
              //         </p>
              //       </div>

              //       <h3 className="text-xl font-semibold text-gray-900 mb-4">Testing Features</h3>
              //       <ul className="list-disc list-inside space-y-2 text-gray-600">
              //         <li>Interactive endpoint testing</li>
              //         <li>Real-time response preview</li>
              //         <li>Parameter validation</li>
              //         <li>Response status codes</li>
              //         <li>JSON formatting and syntax highlighting</li>
              //       </ul>

              //       <h3 className="text-xl font-semibold text-gray-900 mb-4 mt-8">Rate Limits</h3>
              //       <div className="bg-gray-100 rounded-lg p-4">
              //         <ul className="space-y-1 text-sm text-gray-700">
              //           <li><strong>Free tier:</strong> 1,000 requests per day</li>
              //           <li><strong>Pro tier:</strong> 10,000 requests per day</li>
              //           <li><strong>Enterprise:</strong> Custom limits</li>
              //         </ul>
              //       </div>
              //     </div>
              //   </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>

  );
};

export default ApiDocsPage;